import PropTypes from "prop-types";
import React from "react";
import { Alert, Badge, Link, SkeletonLoader, Table } from "@yoast/ui-library";
import { defineMessages, FormattedDate, FormattedMessage } from "react-intl";
import { getScopeLabel } from "./scopeLabel";
import * as styles from "./PersonalAccessTokensPageStyles.module.scss";

const messages = defineMessages( {
	empty: {
		id: "personalAccessTokens.list.empty",
		defaultMessage: "You have not created any personal access tokens yet.",
	},
	headerName: {
		id: "personalAccessTokens.list.header.name",
		defaultMessage: "Name",
	},
	headerToken: {
		id: "personalAccessTokens.list.header.token",
		defaultMessage: "Token",
	},
	headerScopes: {
		id: "personalAccessTokens.list.header.scopes",
		defaultMessage: "Permissions",
	},
	headerCreated: {
		id: "personalAccessTokens.list.header.created",
		defaultMessage: "Created",
	},
	headerLastUsed: {
		id: "personalAccessTokens.list.header.lastUsed",
		defaultMessage: "Last used",
	},
	headerExpires: {
		id: "personalAccessTokens.list.header.expires",
		defaultMessage: "Expires",
	},
	headerActions: {
		id: "personalAccessTokens.list.header.actions",
		defaultMessage: "Actions",
		description: "Visually hidden header label for the actions column.",
	},
	revoke: {
		id: "personalAccessTokens.list.revoke",
		defaultMessage: "Revoke",
	},
	never: {
		id: "personalAccessTokens.list.never",
		defaultMessage: "Never",
	},
	noScopes: {
		id: "personalAccessTokens.list.noScopes",
		defaultMessage: "(none)",
	},
} );

/**
 * Renders a date or a "never" fallback.
 *
 * @param {string|null} value The date to render.
 * @param {Object} fallback The intl message for the fallback.
 *
 * @returns {JSX.Element} The rendered date or fallback.
 */
function renderDate( value, fallback ) {
	if ( ! value ) {
		return <FormattedMessage { ...fallback } />;
	}
	return <FormattedDate value={ value } year="numeric" month="short" day="numeric" />;
}

/**
 * A placeholder row shown while the tokens are being loaded for the first time.
 *
 * The widths roughly mirror the real cell content so the layout stays stable
 * once data arrives.
 *
 * @returns {JSX.Element} A skeleton table row.
 */
function SkeletonRow() {
	return (
		<Table.Row>
			<Table.Cell><SkeletonLoader>My token name</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>myp_12345678_*****</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Sign software statements</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Jan 1, 1970</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Jan 1, 1970</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Never</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Revoke</SkeletonLoader></Table.Cell>
		</Table.Row>
	);
}

/**
 * Personal access tokens list.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The list (or its loading/empty/error state).
 */
function PersonalAccessTokensList(
	{
		tokens,
		loadErrorMessage,
		onRevokeClick,
		availableScopes = [],
		isLoading = false,
		error = null,
	},
) {
	if ( error ) {
		return (
			<Alert variant="error">
				<FormattedMessage { ...loadErrorMessage } />
			</Alert>
		);
	}

	if ( ! isLoading && tokens.length === 0 ) {
		return (
			<p className={ styles.empty }>
				<FormattedMessage { ...messages.empty } />
			</p>
		);
	}

	return (
		<div className={ styles.tableContainer }>
			<Table>
				<Table.Head className={ styles.tableHead }>
					<Table.Row>
						<Table.Header><FormattedMessage { ...messages.headerName } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerToken } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerScopes } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerCreated } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerLastUsed } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerExpires } /></Table.Header>
						<Table.Header>
							<span className="yst-sr-only">
								<FormattedMessage { ...messages.headerActions } />
							</span>
						</Table.Header>
					</Table.Row>
				</Table.Head>
				<Table.Body className={ styles.tableBody }>
					{ isLoading
						? <SkeletonRow />
						: tokens.map( token => (
							<Table.Row key={ token.id }>
								<Table.Cell data-label={ messages.headerName.defaultMessage }>{ token.name }</Table.Cell>
								<Table.Cell data-label={ messages.headerToken.defaultMessage }>
									<code className={ styles.prefix }>{ token.prefix }_*****</code>
								</Table.Cell>
								<Table.Cell data-label={ messages.headerScopes.defaultMessage }>
									{ ( token.scopes || [] ).length === 0
										? <FormattedMessage { ...messages.noScopes } />
										: (
											<span className={ styles.scopeBadges }>
												{ token.scopes.map( scope => (
													<Badge key={ scope } variant="plain" size="small">
														{ getScopeLabel( scope, availableScopes ) }
													</Badge>
												) ) }
											</span>
										)
									}
								</Table.Cell>
								<Table.Cell data-label={ messages.headerCreated.defaultMessage }>
									{ renderDate( token.createdAt, messages.never ) }
								</Table.Cell>
								<Table.Cell data-label={ messages.headerLastUsed.defaultMessage }>
									{ renderDate( token.lastUsedAt, messages.never ) }
								</Table.Cell>
								<Table.Cell data-label={ messages.headerExpires.defaultMessage }>
									{ renderDate( token.expiresAt, messages.never ) }
								</Table.Cell>
								<Table.Cell>
									<Link
										as="button"
										type="button"
										variant="error"
										onClick={ () => onRevokeClick( token ) }
									>
										<FormattedMessage { ...messages.revoke } />
									</Link>
								</Table.Cell>
							</Table.Row>
						) ) }
				</Table.Body>
			</Table>
		</div>
	);
}

PersonalAccessTokensList.propTypes = {
	tokens: PropTypes.arrayOf( PropTypes.shape( {
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		prefix: PropTypes.string.isRequired,
		scopes: PropTypes.arrayOf( PropTypes.string ),
		createdAt: PropTypes.string,
		lastUsedAt: PropTypes.string,
		expiresAt: PropTypes.string,
	} ) ).isRequired,
	isLoading: PropTypes.bool,
	error: PropTypes.object,
	loadErrorMessage: PropTypes.object.isRequired,
	availableScopes: PropTypes.arrayOf( PropTypes.shape( {
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	} ) ),
	onRevokeClick: PropTypes.func.isRequired,
};

export default PersonalAccessTokensList;
